import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Form, DatePicker, Space, Input, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import Cookies from 'js-cookie';
import * as _ from 'lodash';
import TSTable from '../common/TSTable';
import ActionButtons from '../workflow/ActionButtons';
import { Toast } from '../../utils/Toast';
import { URL_WORKFLOW } from '../../constance/urls';
import { PATH } from '../../routes/Routes';
import { formatComma } from '../../utils/stringFormat';
import { DATE_FORMAT, HUMAN_DATE_FORMAT } from '../../constance/strings';
import useWorkflowDetail from '../../hooks/useWorkflowDetail';
import { useDebounce } from 'use-debounce';
import PropTypes from 'prop-types';
import ColorButton from '../common/ColorButton';
import { BLUE_COLOR } from '../../constance/color';

const AVAILABLE_ACTIONS = {
  manager: ['manager_approve', 'manager_reject'],
  md: ['md_approve', 'md_reject'],
};

function LogWoodPOList(props) {
  const { t } = useTranslation();
  const history = useHistory();
  const tableRef = useRef();
  const [selectedList, setSelectedList] = useState([]);
  const [user] = useState(JSON.parse(Cookies.get('user', '{}')));
  const [form] = Form.useForm();
  const date = Form.useWatch('date', form);
  const searchText = Form.useWatch('search', form);
  const [debouncedText] = useDebounce(searchText, 500);
  const [data, setData] = React.useState('');

  const formattedDate = date ? dayjs(date).format(DATE_FORMAT) : null;

  const { handleAction, isLoading, errorMessages } = useWorkflowDetail({
    id: selectedList,
    workflowUrl: URL_WORKFLOW.LOG_WOOD_PO_WORKFLOW,
    manual: true,
    multiple: true,
    onSuccess: () => {
      if (tableRef.current) {
        tableRef.current.fetch();
        Toast.success(t('success'));
      }
    },
  });

  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.fetch?.();
    }
  }, [date, debouncedText]);

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedList(selectedRowKeys)
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === 'Disabled User',
      name: record.name,
    }),
  };

  const columns = useMemo(
    () => [
      {
        title: t('truck_transactions.ticket1'),
        dataIndex: ['truck_transaction', 'ticket1'],
      },
      {
        title: t('truck_transactions.ticket2'),
        dataIndex: ['truck_transaction', 'ticket2'],
      },
      {
        title: t('truck_transactions.in_date_time'),
        dataIndex: ['truck_transaction', 'in_date_time'],
        render: (value) => dayjs(value).format('DD/MM/YYYY HH:mm:ss'),
      },
      {
        title: t('truck_transactions.out_date_time'),
        dataIndex: ['truck_transaction', 'out_date_time'],
        render: (value) => value ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : '-',
      },
      {
        title: t('trucks.license_plate'),
        dataIndex: ['truck_transaction', 'license_plate'],
      },
      {
        title: t('truck_transactions.company'),
        dataIndex: ['truck_transaction', 'truck_company_detail'],
        render: (company) => (company ? `[${company.code}] ${company.name}` : '-'),
      },
      {
        title: t('truck_transactions.in_weight'),
        dataIndex: ['truck_transaction', 'in_weight'],
        render: (value) => formatComma(value),
      },
      {
        title: t('truck_transactions.out_weight'),
        dataIndex: ['truck_transaction', 'out_weight'],
        render: (value) => formatComma(value),
      },
      {
        title: t('log_wood_evaluation_datas.cut_weight'),
        dataIndex: ['truck_transaction', 'cut_weight'],
        render: (value) => formatComma(value),
      },
      {
        title: t('truck_transactions.net_weight'),
        dataIndex: ['truck_transaction', 'net_weight'],
        render: (value) => formatComma(value),
      },
      {
        title: t('log_wood_evaluation_datas.buy_price'),
        dataIndex: ['truck_transaction', 'buy_price'],
        render: (value) => formatComma(value),
      },
      {
        title: t('log_wood_evaluation_datas.buy_amount'),
        dataIndex: ['truck_transaction', 'buy_amount'],
        render: (value) => formatComma(value),
      },
      {
        title: t('detail'),
        dataIndex: 'id',
        render: (id) => (
          <ColorButton
            variant="outlined"
            color='primary'
            override={BLUE_COLOR}
            onClick={() => history.push(`${PATH.LOG_WOOD_PO}${id}`)}
          >
            {t('detail')}
          </ColorButton>
        ),
      },
    ],
    [t]
  );

  const [selectionType, setSelectionType] = useState('checkbox');

  return (
    <div>
      {errorMessages && (
        <Alert
          message={t('error')}
          description={errorMessages}
          type="error"
          showIcon
          style={{ marginBottom: '1rem' }}
        />
      )}

      <ActionButtons
        actions={AVAILABLE_ACTIONS[_.get(user.groups, '0', '')]}
        handleAction={handleAction}
        disabled={!selectedList.length}
        isLoading={isLoading}
      />

      <Form form={form}>
        <Space style={{ width: '100%', textAlign: 'left' }}>
          <Form.Item label={t('searchs.fields.date')} name={'date'}>
            <DatePicker
              format={HUMAN_DATE_FORMAT}
              placeholder={t('searchs.fields.date')}
              style={{ width: 200 }}
            />
          </Form.Item>

          <Form.Item label={t('search')} name={'search'}>
            <Input placeholder={t('search')} allowClear />
          </Form.Item>
        </Space>
      </Form>

      <TSTable
        scroll={{ x: 1000 }}
        rowKey="id"
        dataSource={data}
        rowSelection={{
          type: selectionType,
          ...rowSelection,
        }}
        ref={tableRef}
        url={URL_WORKFLOW.LOG_WOOD_PO_WORKFLOW}
        columns={columns}
        extraFilterParams={{ date }}
        params={{
          date: formattedDate,
          search: searchText,
        }}
      />
    </div>
  );
}

export default LogWoodPOList;
